import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { detailStrings } from "../i18n/detail";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";

export default function ProjectDetail() {
  const { id } = useParams();
  const { lang, t } = useLanguage();
  const d = detailStrings[lang] || detailStrings.pt;
  const [project, setProject] = useState(undefined);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProject(undefined);
    api
      .get("/projects")
      .then((r) => setProject(r.data.find((p) => p.id === id) || null))
      .catch(() => setProject(null));
  }, [id]);

  if (project === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-basalt" data-testid="project-detail-loading">
        <p className="font-mono text-xs tracking-widest text-mist">{d.loading}</p>
      </div>
    );
  }

  if (project === null) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-basalt" data-testid="project-detail-notfound">
        <p className="font-mono text-xs tracking-widest text-mist">{d.notFound}</p>
        <Link to="/" state={{ scrollTo: "#projetos" }} className="font-mono text-[11px] uppercase tracking-widest text-terracotta" data-testid="project-detail-back-notfound">
          ← {d.back}
        </Link>
      </div>
    );
  }

  const chapters = [
    { title: d.chapters[0], text: lang === "pt" ? project.context_pt : project.context_en },
    { title: d.chapters[1], text: lang === "pt" ? project.strategy_pt : project.strategy_en },
    { title: d.chapters[2], text: lang === "pt" ? project.value_pt : project.value_en },
  ].filter((c) => c.text);

  const description = lang === "pt" ? project.description_pt : project.description_en;
  const statusLabel = t.status[project.status] || project.status;
  const ficha = [
    { label: d.location, value: project.location },
    { label: d.year, value: project.year },
    { label: d.typology, value: project.typology },
    { label: d.status, value: project.status_detail ? `${statusLabel} — ${project.status_detail}` : statusLabel },
  ].filter((f) => f.value);

  return (
    <div className="min-h-screen bg-basalt font-sans text-sand" data-testid="project-detail-page">
      <Navbar />

      <section className="relative flex h-[70vh] min-h-[440px] items-end overflow-hidden" data-testid="project-detail-hero">
        <motion.img
          src={project.image}
          alt={project.name}
          initial={{ scale: 1.08, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="project-detail-image"
        />
        <div className="absolute inset-0 bg-basalt/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-basalt via-basalt/30 to-basalt/40" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-14">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <Link
              to="/"
              state={{ scrollTo: "#projetos" }}
              data-testid="project-detail-back"
              className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-sand/70 transition-colors hover:text-terracotta"
            >
              <ArrowLeft size={13} /> {d.back}
            </Link>
            <h1 className="font-display text-4xl font-medium leading-[1.08] tracking-tight text-sand drop-shadow-[0_2px_14px_rgba(13,17,23,0.9)] sm:text-5xl lg:text-6xl" data-testid="project-detail-title">
              {project.name}
            </h1>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-sand/70" data-testid="project-detail-meta">
              {[project.location, project.year, project.typology].filter(Boolean).join("  ·  ")}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="border-b border-white/10 py-14" data-testid="project-detail-ficha">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="mb-8 font-mono text-[11px] uppercase tracking-[0.35em] text-terracotta">{d.ficha}</p>
          </Reveal>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 lg:grid-cols-4">
            {ficha.map((f, i) => (
              <Reveal key={f.label} delay={0.05 + i * 0.06} y={16} className="h-full">
                <div className="h-full bg-basalt p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mist">{f.label}</p>
                  <p className="mt-2 font-display text-lg text-sand sm:text-xl">{f.value}</p>
                </div>
              </Reveal>
            ))}
          </div>
          {(project.credit_pt || project.credit_en) && (
            <Reveal delay={0.2} y={16}>
              <div className="mt-8 flex items-center gap-4" data-testid="project-detail-credit">
                <img src="/uaid-logo.png" alt="UAID Arquitetos" className="h-9 w-auto" />
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-mist">
                  {lang === "pt" ? project.credit_pt : project.credit_en}
                </p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {project.for_sale && (
        <section className="border-b border-white/10 bg-surface/40 py-12" data-testid="project-detail-forsale">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-6">
            <p className="font-display text-xl text-sand sm:text-2xl">{d.forSale}</p>
            <a
              href={`https://wa.me/351916927419?text=${encodeURIComponent(`${d.waText} ${project.name}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="project-detail-forsale-cta"
              className="inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3 text-sm font-medium text-basalt transition-colors hover:bg-sand"
            >
              <MessageCircle size={16} /> {d.forSaleCta}
            </a>
          </div>
        </section>
      )}

      <section className="py-20 sm:py-24" data-testid="project-detail-chapters">
        <div className="mx-auto max-w-5xl px-6">
          {chapters.length > 0 ? (
            chapters.map((c, i) => (
              <Reveal key={c.title} delay={0.05} y={24}>
                <div className={`grid gap-4 py-10 sm:grid-cols-12 sm:gap-10 ${i > 0 ? "border-t border-white/10" : ""}`}>
                  <span className="font-mono text-sm text-terracotta sm:col-span-2">(0{i + 1})</span>
                  <h2 className="font-display text-xl text-sand sm:col-span-4 sm:text-2xl">{c.title}</h2>
                  <p className="text-sm leading-relaxed text-mist sm:col-span-6 sm:text-base">{c.text}</p>
                </div>
              </Reveal>
            ))
          ) : (
            description && (
              <Reveal>
                <p className="max-w-3xl font-display text-lg italic leading-relaxed text-sand/90 sm:text-xl">{description}</p>
              </Reveal>
            )
          )}
        </div>
      </section>

      {project.gallery && project.gallery.length > 0 && (
        <section className="border-t border-white/10 py-20 sm:py-24" data-testid="project-detail-gallery">
          <div className="mx-auto max-w-7xl px-6">
            <Reveal>
              <p className="mb-10 font-mono text-[11px] uppercase tracking-[0.35em] text-terracotta">{d.gallery}</p>
            </Reveal>
            <div className="grid gap-5 sm:grid-cols-2">
              {project.gallery.map((src, i) => (
                <Reveal key={src} delay={0.05 * (i % 2)} y={24}>
                  <div className="group overflow-hidden rounded-lg border border-white/10">
                    <img
                      src={src}
                      alt={`${project.name} — ${i + 1}`}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      data-testid={`project-detail-gallery-img-${i}`}
                    />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
